import React, { useState } from 'react';
import { useArchetypeQuiz } from '../../hooks/useArchetypeQuiz';
import QuestionCard from './QuestionCard';
import ArchetypeResult from './ArchetypeResult';
import ProgressIndicator from './ProgressIndicator';
import { FaSpinner, FaArrowLeft, FaArrowRight, FaCheck } from 'react-icons/fa';

const ArchetypeQuiz = ({ onComplete }) => {
    const {
        questions,
        responses,
        currentQuestionIndex,
        currentQuestion,
        loading,
        submitting,
        error,
        result,
        progress,
        isComplete,
        handleAnswer,
        nextQuestion,
        previousQuestion,
        goToQuestion,
        submitQuiz,
        resetQuiz,
        getUnansweredQuestions,
        verifyAllQuestionsAnswered,
        setError
    } = useArchetypeQuiz();

    const [showResult, setShowResult] = useState(false);

    // Manejar respuesta y avanzar
    const handleAnswerAndNext = (questionId, answer) => {
        // Primero actualizar las respuestas
        const updatedResponses = { ...responses, [questionId]: answer };

        // Verificar si todas las preguntas están contestadas con las respuestas actualizadas
        const allAnswered = questions.every(question => updatedResponses[question.id] !== undefined);

        // Debug: Verificar qué preguntas faltan (solo en desarrollo)
        if (process.env.NODE_ENV === 'development') {
            console.log('Updated responses:', updatedResponses);
            console.log('All answered:', allAnswered);

            const missingQuestions = questions.filter(question => updatedResponses[question.id] === undefined);
            if (missingQuestions.length > 0) {
                console.log('Missing questions:', missingQuestions.map(q => ({ id: q.id, question: q.question })));
            }
        }

        // Actualizar el estado de respuestas
        handleAnswer(questionId, answer);

        // Si todas están contestadas, enviar automáticamente
        if (allAnswered) {
            if (process.env.NODE_ENV === 'development') {
                console.log('🎉 All questions answered! Sending automatically...');
            }
            setTimeout(() => {
                handleSubmit(updatedResponses);
            }, 500); // Pequeño delay para que el usuario vea que se completó
        } else {
            // Si es la última pregunta pero no están todas contestadas, mostrar error
            if (currentQuestionIndex === questions.length - 1) {
                const verification = verifyAllQuestionsAnswered(updatedResponses, questions);
                const errorMessage = `Faltan ${verification.unansweredQuestions.length} pregunta(s) por contestar. Por favor, completa todas las preguntas antes de enviar.`;
                setError(errorMessage);
            } else {
                // Avanzar a la siguiente pregunta después de un breve delay
                setTimeout(() => {
                    nextQuestion();
                }, 300);
            }
        }
    };

    // Enviar cuestionario
    const handleSubmit = async (customResponses = null) => {
        try {
            if (process.env.NODE_ENV === 'development') {
                console.log('Submitting quiz with responses:', customResponses || responses);
            }
            const result = await submitQuiz(customResponses);
            setShowResult(true);
            if (onComplete) {
                onComplete(result);
            }
        } catch (error) {
            console.error('Error submitting quiz:', error);
            // Si hay error, no mostrar el modal de error, solo log
            // El error ya se maneja en el hook
        }
    };

    // Reiniciar cuestionario
    const handleReset = () => {
        resetQuiz();
        setShowResult(false);
    };

    // Si está cargando
    if (loading) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 text-center">
                    <FaSpinner className="animate-spin text-blue-500 text-4xl mx-auto mb-4" />
                    <h2 className="text-xl font-semibold mb-2">Cargando configuración...</h2>
                    <p className="text-gray-600">Preparando la configuración inicial</p>
                </div>
            </div>
        );
    }

    // Si hay error de validación
    if (error === 'validation') {
        const unansweredQuestions = getUnansweredQuestions();
        const firstUnansweredQuestion = unansweredQuestions[0];
        const firstUnansweredIndex = questions.findIndex(q => q.id === firstUnansweredQuestion.id);

        return (
            <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
                <div className="text-red-500 text-4xl mb-4">⚠️</div>
                <h2 className="text-xl font-semibold mb-4">
                    Preguntas sin Responder
                </h2>
                <div className="mb-6">
                    <p className="text-gray-600 mb-4">
                        Por favor, responde todas las preguntas para continuar:
                    </p>
                    <ul className="list-disc pl-5 text-gray-600">
                        {unansweredQuestions.map((q, i) => (
                            <li key={i}>Pregunta {q + 1}</li>
                        ))}
                    </ul>
                </div>

                <div className="flex flex-col space-y-3">
                    <button
                        onClick={() => {
                            goToQuestion(firstUnansweredIndex);
                            setError(null);
                        }}
                        className="bg-orange-500 text-white px-8 py-3 rounded-lg hover:bg-orange-600 transition-colors font-medium"
                    >
                        Rellena Todas las Preguntas para Continuar
                    </button>
                </div>
            </div>
        );
    }

    // Para otros tipos de errores
    if (error) {
        const unansweredQuestions = getUnansweredQuestions();
        const isUnansweredError = error.includes('Faltan') && error.includes('pregunta(s) por contestar');

        // Si es error de preguntas no contestadas, mostrar modal específico
        if (isUnansweredError && unansweredQuestions.length > 0) {
            const firstUnansweredQuestion = unansweredQuestions[0];
            const firstUnansweredIndex = questions.findIndex(q => q.id === firstUnansweredQuestion.id);

            return (
                <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 text-center">
                    <div className="text-orange-500 text-4xl mb-4">⚠️</div>
                    <h2 className="text-xl font-semibold mb-2 text-gray-800">
                        Preguntas Pendientes
                    </h2>
                    <p className="text-gray-600 mb-4">
                        Faltan {unansweredQuestions.length} pregunta(s) por contestar para completar la configuración.
                    </p>

                    <div className="mb-6 text-left bg-orange-50 p-4 rounded-lg">
                        <p className="text-sm font-medium text-orange-800 mb-2">Preguntas no contestadas:</p>
                        <ul className="text-sm text-orange-700 space-y-1">
                            {unansweredQuestions.slice(0, 3).map((question, index) => (
                                <li key={index} className="flex items-start">
                                    <span className="text-orange-500 mr-2">•</span>
                                    <span>{question.question}</span>
                                </li>
                            ))}
                            {unansweredQuestions.length > 3 && (
                                <li className="text-orange-600 italic">
                                    ... y {unansweredQuestions.length - 3} más
                                </li>
                            )}
                        </ul>
                    </div>

                    <div className="flex flex-col space-y-3">
                        <button
                            onClick={() => {
                                // Ir a la primera pregunta no contestada
                                goToQuestion(firstUnansweredIndex);
                                // Limpiar el error sin recargar la página
                                setError(null);
                            }}
                            className="bg-orange-500 text-white px-8 py-3 rounded-lg hover:bg-orange-600 transition-colors font-medium"
                        >
                            Rellena Todas las Preguntas para Continuar
                        </button>
                    </div>
                </div>
            );
        }

        // Para otros tipos de errores
        return (
            <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 text-center">
                <div className="text-red-500 text-4xl mb-4">⚠️</div>
                <h2 className="text-xl font-semibold mb-2">Error</h2>
                <p className="text-gray-600 mb-4">{error}</p>
                <button
                    onClick={() => window.location.reload()}
                    className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition-colors"
                >
                    Reintentar
                </button>
            </div>
        );
    }

    // Si se muestra el resultado
    if (showResult && result) {
        return (
            <ArchetypeResult
                archetype={result}
                onComplete={onComplete}
                onReset={handleReset}
            />
        );
    }

    // Si no hay preguntas
    if (!questions || questions.length === 0) {
        return (
            <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 text-center">
                <div className="text-gray-500 text-4xl mb-4">❓</div>
                <h2 className="text-xl font-semibold mb-2">Error de configuración</h2>
                <p className="text-gray-600 mb-4">No se pudieron cargar las preguntas de configuración</p>
                <button
                    onClick={() => window.location.reload()}
                    className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition-colors"
                >
                    Reintentar
                </button>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex justify-center items-center mb-6">
                <h2 className="text-xl font-semibold text-center">
                    Configuración Inicial
                </h2>
            </div>

            {/* Progress Indicator */}
            <ProgressIndicator
                current={currentQuestionIndex + 1}
                total={questions.length}
                progress={progress}
                responses={responses}
                onQuestionClick={goToQuestion}
            />

            {/* Question Card */}
            {currentQuestion ? (
                <QuestionCard
                    question={currentQuestion}
                    currentAnswer={responses[currentQuestion.id]}
                    onAnswer={handleAnswerAndNext}
                    questionNumber={currentQuestionIndex + 1}
                    totalQuestions={questions.length}
                />
            ) : null}
        </div>
    );
};

export default ArchetypeQuiz; 